<template>
  <div>
    <v-dialog v-model="verificationDialog" max-width="600" persistent>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          Image Verification
          <v-spacer></v-spacer>
          <v-btn small icon @click="closeVerificationDialog()">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="mt-3">
          <!-- Image Preview -->
          <div class="text-center mb-4 image-container">
            <transition name="fade">
              <v-skeleton-loader
                v-if="!profilePreview"
                key="skeleton"
                type="image"
                height="200"
                width="200"
                class="mx-auto"
              ></v-skeleton-loader>

              <v-img
                v-else
                :lazy-src="profilePreview"
                key="image"
                :src="profilePreview"
                max-height="200"
                max-width="200"
                class="mx-auto rounded-lg elevation-3"
                contain
              >
                <template v-slot:placeholder>
                  <v-row
                    class="fill-height ma-0"
                    align="center"
                    justify="center"
                  >
                    <v-progress-circular
                      indeterminate
                      color="primary"
                    ></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
            </transition>
          </div>

          <div v-if="currentItem.status == 'verified'" class="d-flex justify-center"><v-chip :color="getStatusColor(currentItem.status)" small>
            Verified
          </v-chip></div>

          <!-- Verification Details -->
          <v-row class="justify-space-between">
            <v-col cols="6">
              <v-list dense>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-card-account-details</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>USN</v-list-item-title>
                    <v-list-item-subtitle>{{
                      currentItem.usn_id
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-calendar</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Submitted At</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ formatDate(currentItem.submitted_at) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>

            <v-col cols="6">
              <v-list dense>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-account</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Name</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ currentItem.student_info?.name }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="currentItem.status == 'verified'">
                  <v-list-item-icon>
                    <v-icon>mdi-calendar</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Verified At</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ formatDate(currentItem.verified_at) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
          <!-- Rejection Reason (Conditional) -->
          <v-textarea
            v-if="selectedAction === 'reject'"
            v-model="rejectionReason"
            label="Reason for Rejection"
            placeholder="Please specify why the photo is being rejected..."
            rows="3"
            outlined
            dense
            :rules="[
              (v) => !!v || 'Reason is required for rejection',
              (v) =>
                (v && v.length >= 10) ||
                'Reason must be at least 10 characters',
            ]"
            class="mt-4"
          ></v-textarea>

          <!-- Action Buttons -->
          <div
            v-if="currentItem.status != 'verified'"
            class="d-flex justify-end mt-4"
          >
            <v-btn
              color="green darken-1 white--text"
              class="ml-2"
              :loading="approving"
              @click="verifyImage('verified')"
            >
              <v-icon left>mdi-check</v-icon>
              Approve
            </v-btn>

            <v-btn
              color="red darken-1 white--text"
              class="ml-2"
              @click="toggleRejectionReason"
            >
              <v-icon left>mdi-close</v-icon>
              Reject
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmRejectDialog" max-width="400">
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          Confirm Rejection
          <v-spacer></v-spacer>
          <v-btn small icon @click="closeVerificationDialog">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="mt-3">
          Are you sure you want to reject this profile photo?
          <v-textarea
            v-model="rejectionReason"
            label="Reason for rejection"
            placeholder="Please provide a reason..."
            rows="2"
            outlined
            dense
            class="mt-4"
            :rules="[
              (v) => !!v || 'Reason is required',
              (v) =>
                (v && v.length >= 10) ||
                'Reason must be at least 10 characters',
            ]"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmRejectDialog = false">Cancel</v-btn>
          <v-btn
            color="red white--text"
            :loading="rejecting"
            @click="verifyImage('rejected')"
            :disabled="!rejectionReason || rejectionReason.length < 10"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  props: {
    item: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      profilePreview: null,
      imageNotFound: false,
      loading: false,
      verificationDialog: false,
      confirmRejectDialog: false,
      selectedAction: null,
      rejectionReason: "",
      approving: false,
      rejecting: false,
      currentItem: {},
      verificationData: null,
    };
  },

  computed: {
    getStatusColor() {
      return (status) => {
        switch (status) {
          case "verified":
            return "success";
          case "rejected":
            return "error";
          case "not_verified":
            return "warning";
          default:
            return "grey";
        }
      };
    },
  },

  created() {
    // this.getImage(this.item)
    this.openVerificationDialog();
  },
  methods: {
    async getImage(item) {
      this.loading = true;
      this.currentItem = item;
      this.profilePreview = null;
      this.imageNotFound = false;

      try {
        // First, check if verification exists
        await this.fetchVerificationData(item.usn_id);

        // Then get the image
        const res = await this.$axios.get(`/get_profile_image/${item.usn_id}`, {
          responseType: "blob",
        });
        this.profilePreview = URL.createObjectURL(res.data);
      } catch (err) {
        if (err.response && err.response.status == 404) {
          this.imageNotFound = true;
          this.$store.commit("showSnackbar", {
            message: "Profile image not found",
            color: "error",
          });
        } else {
          this.$store.commit("showSnackbar", {
            message: "Failed to load image",
            color: "error",
          });
        }
      } finally {
        this.loading = false;
      }
    },

    async fetchVerificationData(usnId) {
      try {
        const response = await this.$axios.get(
          `/profile_photo_verifications/latest/${usnId}`
        );
        this.verificationData = response.data;
      } catch (error) {
        console.warn("No verification record found, creating new one");
      }
    },

    openVerificationDialog() {
      this.verificationDialog = true;
    },

    closeVerificationDialog() {
      this.verificationDialog = false;
      this.selectedAction = null;
      this.rejectionReason = "";
      this.$emit("close");
    },

    toggleRejectionReason() {
      if (!this.rejectionReason) {
        this.confirmRejectDialog = true;
      } else {
        this.selectedAction = "reject";
      }
    },

    async verifyImage(status) {
      if (
        status == "rejected" &&
        (!this.rejectionReason || this.rejectionReason.length < 10)
      ) {
        this.$store.commit("showSnackbar", {
          message: "Please provide a reason for rejection (min 10 chars)",
          color: "warning",
        });
        return;
      }

      // Set loading state
      if (status == "verified") {
        this.approving = true;
      } else {
        this.rejecting = true;
      }

      try {
        const formData = {
          profile_photo_verification: {
            usn_id: this.currentItem.usn_id,
            status: status,
            reason: status === "rejected" ? this.rejectionReason : null,
            verified_at: new Date().toISOString(),
            verified_by: this.getCurrentUserId(),
            student_info_id:
              this.currentItem.id || this.currentItem.student_info_id,
            submitted_at:
              this.currentItem.submitted_at || new Date().toISOString(),
          },
        };

        let response;

        if (this.verificationData && this.verificationData.id) {
          // Update existing verification
          response = await this.$axios.put(
            `/profile_photo_verifications/${this.verificationData.id}`,
            formData
          );
        } else {
          response = await this.$axios.post(
            `/profile_photo_verifications`,
            formData
          );
        }

        // Update local data
        this.currentItem.status = status;
        this.verificationData = response.data;

        // Show success message
        this.$store.commit("showSnackbar", {
          message: `Image ${
            status == "verified" ? "approved" : "rejected"
          } successfully`,
          color: "success",
        });

        // Emit event to parent component
        this.$emit("verification-updated", {
          usn_id: this.currentItem.usn_id,
          status: status,
          verification: response.data,
        });

        // Close dialog
        this.closeVerificationDialog();
        this.confirmRejectDialog = false;
      } catch (error) {
        console.error("Verification error:", error);
        this.$store.commit("showSnackbar", {
          message: `Failed to ${
            status === "verified" ? "approve" : "reject"
          } image`,
          color: "error",
        });
      } finally {
        this.approving = false;
        this.rejecting = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return `${String(date.getDate()).padStart(2, "0")}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}-${String(date.getFullYear()).padStart(
        4,
        "0"
      )} ${String(date.getHours()).padStart(2, "0")}:${String(
        date.getMinutes()
      ).padStart(2, "0")}`;
    },

    getCurrentUserId() {
      // Implement based on your auth system
      // Example: return this.$store.state.user.id;
      return 1; // Replace with actual user ID
    },

    // Clean up object URL to prevent memory leaks
    beforeDestroy() {
      if (this.profilePreview) {
        URL.revokeObjectURL(this.profilePreview);
      }
    },
  },

  watch: {
    item: {
      handler(newVal) {
        if (newVal && newVal.usn_id) {
          this.getImage(newVal);
        }
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.7s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.image-container {
  display: flex;
  height: 200px;
  width: 200px;
  margin: 0 auto;
}
</style>
